import {
  Button,
  type ButtonSize,
  DropdownMenu,
  IconButton,
  type IconName,
} from '@wandb/components';
import React from 'react';

type SplitButtonProps = {
  /** Label for the primary (left) button. */
  children: string;
  /** Icon displayed before the label. */
  startIcon?: IconName;
  /** Icon displayed after the label. */
  endIcon?: IconName;
  /** Size applied to both buttons. @default "md" */
  size?: ButtonSize;
  /** Handler for the primary button press. */
  onPress: () => void;
  /** Whether the primary button is disabled. */
  isDisabled?: boolean;
  /** Whether the dropdown trigger is disabled. */
  isMenuDisabled?: boolean;
  /** Dropdown menu content rendered inside the chevron menu. */
  menuContent: React.ReactNode;
};

/**
 * Joins buttons into a single group with shared borders and rounded outer corners.
 * The `!important` border-radius overrides are needed because `@wandb/components`
 * applies border-radius via triple-specificity CSS modules.
 */
function ButtonGroup({children}: {children: React.ReactNode}) {
  return (
    <div className="inline-flex items-center [&_button:not(:first-child)]:!rounded-l-none [&_button:not(:first-child)]:border-l [&_button:not(:first-child)]:border-l-moon-300 [&_button:not(:last-child)]:!rounded-r-none">
      {children}
    </div>
  );
}

/** A secondary-variant button joined with a dropdown chevron for overflow actions. */
export function SplitButton({
  children,
  startIcon,
  endIcon,
  size = 'md',
  onPress,
  isDisabled,
  isMenuDisabled,
  menuContent,
}: SplitButtonProps) {
  return (
    <ButtonGroup>
      <Button
        size={size}
        variant="secondary"
        startIcon={startIcon}
        endIcon={endIcon}
        isDisabled={isDisabled}
        onPress={onPress}>
        {children}
      </Button>
      <DropdownMenu.Trigger>
        <IconButton
          icon="chevron-down"
          size={size}
          variant="secondary"
          aria-label="More actions"
          isDisabled={isMenuDisabled}
        />
        {menuContent}
      </DropdownMenu.Trigger>
    </ButtonGroup>
  );
}
